/**
 * Structured payment-flow logger.
 *
 * Every payment-related stage (client init → booking draft → razorpay order
 * → verify → payment record → booking update) emits a single tagged line.
 *
 * BOTH client AND server lines end up in `logs/payflow.log` at the project
 * root so you can read one full transaction top-to-bottom (or bottom-to-top
 * with `tail`) in a single file:
 *
 *   tail -f logs/payflow.log
 *   tail -n 200 logs/payflow.log
 *
 * How the routing works:
 *   - On the server, payflowLog appends directly to logs/payflow.log AND
 *     also emits to console.error so it surfaces in the hosting dashboard.
 *   - On the client, payflowLog emits to console.log (browser dev tools)
 *     AND POSTs the line to /api/debug/payflow, which writes it to the
 *     same file. Best-effort; failures are silent so a logging hiccup
 *     never breaks the payment flow.
 *
 * Caveats:
 *   - Serverless hosts with ephemeral filesystems (Vercel, etc.) will let
 *     the file write succeed but you'll only see lines from the same lambda
 *     instance, and the file disappears between invocations. Use this
 *     primarily for local dev / a long-running Node host (Railway, Render,
 *     a VM, etc.). The console fallbacks still go to the host's log
 *     dashboard regardless.
 *   - The file is gitignored (logs/).
 *
 * To remove all instrumentation: delete this file + the import + every
 * `payflowLog(...)` call site + the /api/debug/payflow route.
 */

type PayflowStage =
  // Client-side stages (PaymentStep.tsx)
  | 'client:pay-clicked'
  | 'client:draft-response'
  | 'client:charge-calc'
  | 'client:create-order-request'
  | 'client:create-order-response'
  | 'client:razorpay-verified'
  | 'client:payment-success-payload'
  | 'client:payment-failed'
  // Server-side stages
  | 'server:booking-create-input'
  | 'server:booking-create-output'
  | 'server:create-order-input'
  | 'server:create-order-booking-state'
  | 'server:create-order-final'
  | 'server:create-order-razorpay-response'
  | 'server:verify-payment-input'
  | 'server:verify-payment-fetched'
  | 'server:payments-input'
  | 'server:payments-captured-check'
  | 'server:payments-booking-update';

const isClient =
  typeof window !== 'undefined' && typeof (window as any).document !== 'undefined';

/**
 * Server-side file append. Lazy-loads `fs`/`path` so the module bundles
 * cleanly on the client. Best-effort: filesystem errors (read-only fs on
 * serverless, permission denied, ENOSPC) are swallowed.
 */
function serverAppendToFile(line: string) {
  if (isClient) return;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const fs = require('fs');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const path = require('path');
    const logsDir = path.resolve(process.cwd(), 'logs');
    if (!fs.existsSync(logsDir)) {
      fs.mkdirSync(logsDir, { recursive: true });
    }
    fs.appendFileSync(path.join(logsDir, 'payflow.log'), line + '\n', 'utf8');
  } catch {
    // Filesystem unavailable (serverless, read-only, etc.) — fall back is
    // already the console line emitted by the caller.
  }
}

export function payflowLog(stage: PayflowStage, data: Record<string, unknown>) {
  const line = {
    ts: new Date().toISOString(),
    stage,
    ...data,
  };
  const serialized = `[payflow:${stage}] ${JSON.stringify(line)}`;

  if (isClient) {
    // Browser dev tools
    // eslint-disable-next-line no-console
    console.log(`[payflow:${stage}]`, line);
    // Forward to server so it ends up in logs/payflow.log alongside the
    // server-side stages. Fire-and-forget; if it fails the dev console
    // still has the line.
    try {
      void fetch('/api/debug/payflow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(line),
        // keepalive lets the request survive a navigation right after
        // payment success.
        keepalive: true,
      }).catch(() => {});
    } catch {
      // ignore
    }
    return;
  }

  // Server-side: console.error so it always surfaces in the host's log
  // dashboard (matches the existing [booking-pricing] pattern), plus a
  // file append so everything ends up in one place.
  // eslint-disable-next-line no-console
  console.error(serialized);
  serverAppendToFile(serialized);
}

/**
 * Used by the /api/debug/payflow ingest endpoint to write a client-emitted
 * line into the shared file. Skips the console emit since the client
 * already logged it on its side.
 */
export function payflowAppendFromClient(line: unknown) {
  if (isClient) return;
  try {
    const stage =
      typeof line === 'object' && line !== null && 'stage' in line
        ? String((line as any).stage)
        : 'unknown';
    serverAppendToFile(`[payflow:${stage}] ${JSON.stringify(line)}`);
  } catch {
    // ignore
  }
}
